const React = require('react');
const { useState } = React;

const { request, minimizeArrays } = require('../lib/apiClient');
const {
  FUTURE_CONNECTOR_BUTTON_WIDTH,
  FUTURE_CONNECTOR_BUTTON_HEIGHT,
  cloneTemplateNode,
  getFutureNodePosition,
  buildGeneratedCustomTemplate,
  autoFieldName,
  autoSetVariableSourceForTarget
} = require('../lib/canvasUtils');
const { getStartNodeOutput, parseVariableContextValue, isToolAgentNode } = require('../lib/nodeTypes');
const { anchorDir } = require('../lib/connectionPaths');
const {
  isSystemObjectKind,
  getSystemObjectIconFromKind,
  findSystemObjectTemplateByIcon
} = require('../lib/insertNodeKinds');

// Owns the "generate flow with AI" feature: the popup state, the streaming
// "coding…" card, the call to /custom-objects/generate, and the insertion
// of the resulting node + auto-run. Lifted out of WorkflowCanvas to keep
// that file focused on rendering / layout.
function useAiFlowGeneration(deps) {
  var canvasRef = deps.canvasRef;
  var panPosRef = deps.panPosRef;
  var zoomRef = deps.zoomRef;
  var worldRef = deps.worldRef;
  var autoPanAnimRef = deps.autoPanAnimRef;
  var applyWorldStyle = deps.applyWorldStyle;

  var nodes = deps.nodes;
  var nodeById = deps.nodeById;
  var nodeOutputByIdRef = deps.nodeOutputByIdRef;
  var variableValuesByNameRef = deps.variableValuesByNameRef;
  var workflowStateRef = deps.workflowStateRef;
  var executionState = deps.executionState;

  var setWorkflowState = deps.setWorkflowState;
  var setSelectedNodeIds = deps.setSelectedNodeIds;
  var setSelectedConnectionId = deps.setSelectedConnectionId;

  var buildUpstreamNodeFlow = deps.buildUpstreamNodeFlow;

  var props = deps.props || {};

  var [aiPromptState, setAiPromptState] = useState(null);
  var [aiCodingState, setAiCodingState] = useState(null);

  function handleGenerateFlowWithAi(futureConnector, event, opts) {
    // `event` may be a real DOM event (mouse/touch) or, on some touch-gesture
    // paths (onTouchUp), a synthetic object without these methods — guard each.
    if (event) {
      if (typeof event.preventDefault === 'function') event.preventDefault();
      if (typeof event.stopPropagation === 'function') event.stopPropagation();
    }
    opts = opts || {};
    var popupPosition = {
      left: futureConnector.buttonLeft - 95,
      top: futureConnector.buttonTop + FUTURE_CONNECTOR_BUTTON_HEIGHT + 8,
      side: 'left'
    };
    var canvasWidth = canvasRef.current ? canvasRef.current.clientWidth : 1200;
    var canvasHeight = canvasRef.current ? canvasRef.current.clientHeight : 700;
    var popoverWidth = 400;
    // Estimate generously so the bottom-clamp below keeps the whole card —
    // Cancel/Create-node footer included — on screen.
    var popoverHeight = 560;
    var gap = 20;
    var pan = panPosRef.current;
    var z = zoomRef.current;

    // The card is placed off the "What's Next?" pill — not off the click point,
    // which used to let it drift on top of the pill.
    var pillLeftScreen = (futureConnector.buttonLeft * z) + pan.x;
    var pillRightScreen = ((futureConnector.buttonLeft + FUTURE_CONNECTOR_BUTTON_WIDTH) * z) + pan.x;
    var pillMidScreen = ((futureConnector.buttonTop + (FUTURE_CONNECTOR_BUTTON_HEIGHT / 2)) * z) + pan.y;

    var side = (pillRightScreen + gap + popoverWidth > canvasWidth - 10) ? 'right' : 'left';
    var pillEdgeScreen = side === 'left' ? pillRightScreen : pillLeftScreen;

    var left = side === 'left'
      ? pillEdgeScreen + gap
      : pillEdgeScreen - popoverWidth - gap;
    left = Math.max(10, Math.min(left, canvasWidth - popoverWidth - 10));
    var top = Math.max(10, Math.min(pillMidScreen - 110, canvasHeight - popoverHeight - 10));

    // The connector wire leaves the SOURCE NODE's edge, not the pill.
    // `futureConnector.from` is that exact anchor point in canvas coords (and
    // `anchor` which node side it exits), so the wire survives pan and zoom and
    // curves out the same way the node's real edges do. Fall back to the pill
    // edge if it's missing.
    var sourceAnchor = futureConnector.from;
    var exitDir = anchorDir(futureConnector.anchor || (side === 'left' ? 'right' : 'left'));
    var wireAnchor = (sourceAnchor && Number.isFinite(sourceAnchor.x) && Number.isFinite(sourceAnchor.y))
      ? { x: sourceAnchor.x, y: sourceAnchor.y, dirX: exitDir.x, dirY: exitDir.y }
      : {
          x: side === 'left'
            ? futureConnector.buttonLeft + FUTURE_CONNECTOR_BUTTON_WIDTH
            : futureConnector.buttonLeft,
          y: futureConnector.buttonTop + (FUTURE_CONNECTOR_BUTTON_HEIGHT / 2),
          dirX: side === 'left' ? 1 : -1,
          dirY: 0
        };
    var wireAnchorScreenX = (wireAnchor.x * z) + pan.x;
    var wireAnchorScreenY = (wireAnchor.y * z) + pan.y;

    popupPosition = {
      left: Math.round(left),
      top: Math.round(top),
      side: side,
      anchor: wireAnchor,
      offset: {
        left: Math.round(left - wireAnchorScreenX),
        top: Math.round(top - wireAnchorScreenY)
      }
    };

    // The planner's <node agent="..."> decision is authoritative for the final
    // node's type, icon, and naming (finalizeSingleNode uses spec.agentMode). We
    // do NOT seed agentMode from the source node's icon: dragging off an agent
    // node used to default this to true, which leaked an agent persona name +
    // robot icon onto deterministic nodes even when the planner chose agent=false.
    var defaultAgentMode = false;

    // Single nodes never use Opus (the planner's "smartest" tier). Tiers here are
    // Fast (Haiku) / Balanced (Sonnet); default Balanced. Legacy stored 'smartest'
    // is ignored so old prefs can't pull in Opus.
    var savedTier = 'balanced';
    try {
      var stored = localStorage.getItem('alleflow-ai-model-tier');
      if (stored === 'fast' || stored === 'balanced') savedTier = stored;
    } catch (e) {}

    setAiPromptState({
      futureConnector: futureConnector,
      prompt: '',
      loading: false,
      error: '',
      position: popupPosition,
      // Tool nodes default to agent mode with a single fixed "output" response
      // field (fieldsEdited keeps typing from renaming it) — a consistent result
      // field name keeps tools uniform for the calling Tool Agent.
      agentMode: opts.toolMode ? true : defaultAgentMode,
      modelTier: savedTier,
      responseFields: opts.toolMode ? ['output'] : [''],
      fieldsEdited: !!opts.toolMode,
      // Opened from a Tool Agent's "+ add tool" anchor → default to the 'tool'
      // kind (a callable tool node). Otherwise a plain custom node.
      nodeKind: opts.toolMode ? 'tool' : 'custom',
      // Source-node snapshot for the AI Flow Builder chat (probing + Q&A) and
      // for the eventual codegen. Captured at open time. In tool mode the source
      // output is withheld (inputs[0] is agent-supplied, shape <any_shape>).
      singleNodeContext: buildSingleNodeContext(futureConnector, !!opts.toolMode),
      insertConnectionId: opts.insertConnectionId || null,
      insertTargetNodeId: opts.insertTargetNodeId || null,
      insertTargetAnchor: opts.insertTargetAnchor || null,
      insertTargetTitle: opts.insertTargetTitle || '',
      // True when the balloon was opened from a Tool Agent's "+ add tool"
      // connector. Drives whether the "tool node" kind is offered; the dialog
      // shows tool-specific copy and the generated node wires from the bottom
      // anchor (becoming a callable tool) whenever the chosen kind is 'tool'.
      toolMode: !!opts.toolMode
    });
  }

  function handleInsertNodeOnConnection(args) {
    var connection = args.connection;
    var midpoint = args.midpoint;
    var event = args.event;
    if (!connection || !midpoint) return;
    var sourceNode = nodes.find(function(n) { return n && n.id === connection.fromId; });
    var targetNode = nodes.find(function(n) { return n && n.id === connection.toId; });
    var sourceAnchor = connection.fromAnchor || 'right';
    var futureConnector = {
      id: 'insert-' + connection.id,
      nodeId: connection.fromId,
      nodeTitle: sourceNode ? sourceNode.title : 'Node',
      anchor: sourceAnchor,
      to: { x: midpoint.x, y: midpoint.y },
      buttonLeft: midpoint.x - (FUTURE_CONNECTOR_BUTTON_WIDTH / 2),
      buttonTop: midpoint.y - (FUTURE_CONNECTOR_BUTTON_HEIGHT / 2)
    };
    handleGenerateFlowWithAi(futureConnector, event, {
      insertConnectionId: connection.id,
      insertTargetNodeId: connection.toId,
      insertTargetAnchor: connection.toAnchor || null,
      insertTargetTitle: targetNode ? targetNode.title : ''
    });
  }

  function closeAiPrompt() {
    setAiPromptState(null);
  }

  function updateAiPrompt(value) {
    setAiPromptState(function(prev) {
      if (!prev) return prev;
      var updates = { prompt: value, error: '' };
      if (prev.agentMode && !prev.fieldsEdited) {
        updates.responseFields = [autoFieldName(value)];
      }
      return Object.assign({}, prev, updates);
    });
  }

  function updateAiAgentMode(enabled) {
    setAiPromptState(function(prev) {
      if (!prev) return prev;
      // Tool nodes keep the fixed "output" result field regardless of the toggle.
      var isToolKind = prev.nodeKind === 'tool';
      return Object.assign({}, prev, {
        agentMode: enabled,
        responseFields: isToolKind ? ['output'] : (enabled ? [autoFieldName(prev.prompt)] : ['']),
        fieldsEdited: isToolKind
      });
    });
  }

  function updateAiModelTier(tier) {
    try { localStorage.setItem('alleflow-ai-model-tier', tier); } catch (e) {}
    setAiPromptState(function(prev) {
      if (!prev) return prev;
      return Object.assign({}, prev, { modelTier: tier });
    });
  }

  function updateAiResponseFields(fields) {
    setAiPromptState(function(prev) {
      if (!prev) return prev;
      return Object.assign({}, prev, { responseFields: Array.isArray(fields) ? fields : [''], fieldsEdited: true });
    });
  }

  function updateAiNodeKind(nextKind) {
    setAiPromptState(function(prev) {
      if (!prev) return prev;
      // Switching to/from the 'tool' kind changes the source-output contract
      // (tool nodes get an agent-supplied <any_shape> input, so sourceOutput is
      // withheld). Rebuild the single-node context to match the new kind.
      var isTool = nextKind === 'tool';
      var wasTool = prev.nodeKind === 'tool';
      var nextContext = buildSingleNodeContext(prev.futureConnector, isTool);
      return Object.assign({}, prev, {
        nodeKind: nextKind,
        singleNodeContext: nextContext,
        // Entering the tool kind applies the tool defaults (agent mode on, fixed
        // "output" result field); leaving it restores the plain-node defaults.
        // Switches between non-tool kinds leave the toggle/fields untouched.
        agentMode: isTool ? true : (wasTool ? false : prev.agentMode),
        responseFields: isTool ? ['output'] : (wasTool ? [''] : prev.responseFields),
        fieldsEdited: isTool ? true : (wasTool ? false : prev.fieldsEdited),
        error: ''
      });
    });
  }

  function buildGeneratedTemplate(data, generationPrompt, options) {
    var opts = options || {};
    var base = Object.assign({}, buildGeneratedCustomTemplate(data), {
      generationPrompt: generationPrompt,
      changeHistory: []
    });
    if (opts.agentMode) {
      return Object.assign({}, base, { icon: 'agent', category: 'custom' });
    }
    return base;
  }

  function createNodeFromAiTemplate(template, futureConnector, insertContext) {
    if (!nodeById[futureConnector.nodeId]) return;
    insertContext = insertContext || null;

    var targetAnchor = 'left';
    if (futureConnector.anchor === 'left') targetAnchor = 'right';
    else if (futureConnector.anchor === 'top') targetAnchor = 'bottom';
    else if (futureConnector.anchor === 'bottom') targetAnchor = 'top';

    var newPos = getFutureNodePosition(template, futureConnector);
    var newNodeId = template.id + '-node-' + Date.now().toString(36);
    var newConnectionId = 'manual-conn-' + Date.now().toString(36);
    var spliceConnectionId = 'manual-conn-' + Date.now().toString(36) + '-splice';
    var computedNextState = null;

    setWorkflowState(function(prev) {
      var existing = (prev.nodes || []).some(function(node) { return node.id === newNodeId; });
      if (existing) { computedNextState = prev; return prev; }
      var cloned = cloneTemplateNode(template, newNodeId, newPos);
      if (template._agentConfig) {
        cloned.paramValues = Object.assign({}, cloned.paramValues || {}, template._agentConfig);
      }
      var nextNodes = autoSetVariableSourceForTarget((prev.nodes || []).concat(cloned), newNodeId, false);
      var prevConnections = prev.connections || [];

      var nextConnections;
      if (insertContext && insertContext.insertConnectionId && insertContext.insertTargetNodeId) {
        var filtered = prevConnections.filter(function(c) { return c.id !== insertContext.insertConnectionId; });
        nextConnections = filtered.concat([
          {
            id: newConnectionId,
            from: futureConnector.nodeId,
            to: newNodeId,
            fromAnchor: futureConnector.anchor,
            toAnchor: targetAnchor
          },
          {
            id: spliceConnectionId,
            from: newNodeId,
            to: insertContext.insertTargetNodeId,
            fromAnchor: targetAnchor === 'left' ? 'right' : (targetAnchor === 'right' ? 'left' : (targetAnchor === 'top' ? 'bottom' : 'top')),
            toAnchor: insertContext.insertTargetAnchor || 'left'
          }
        ]);
      } else {
        nextConnections = prevConnections.concat({
          id: newConnectionId,
          from: futureConnector.nodeId,
          to: newNodeId,
          fromAnchor: futureConnector.anchor,
          toAnchor: targetAnchor
        });
      }

      var next = Object.assign({}, prev, {
        nodes: nextNodes,
        connections: nextConnections
      });
      computedNextState = next;
      return next;
    });

    var sel = {}; sel[newNodeId] = true;
    setSelectedNodeIds(sel);
    setSelectedConnectionId(null);
    autoRunNewNode(newNodeId, computedNextState);
    return newNodeId;
  }

  function autoRunNewNode(_newNodeId, _overrideState) {
    // Auto-run on node create/generate is disabled — user runs the node manually.
  }

  function autoPanToCodeCard(pos) {
    var canvas = canvasRef.current;
    if (!canvas || !worldRef.current) return;
    var rect = canvas.getBoundingClientRect();
    var zoom = zoomRef.current;
    var cardCenterX = pos.left + 130;
    var cardCenterY = pos.top + 85;
    var targetPanX = rect.width * 0.55 - cardCenterX * zoom;
    var targetPanY = rect.height * 0.5  - cardCenterY * zoom;
    var startPanX = panPosRef.current.x;
    var startPanY = panPosRef.current.y;
    var startTime = null;
    var DURATION = 480;
    if (autoPanAnimRef.current) cancelAnimationFrame(autoPanAnimRef.current);
    function step(ts) {
      if (!startTime) startTime = ts;
      var t = Math.min((ts - startTime) / DURATION, 1);
      var ease = 1 - Math.pow(1 - t, 3);
      var x = startPanX + (targetPanX - startPanX) * ease;
      var y = startPanY + (targetPanY - startPanY) * ease;
      panPosRef.current = { x: x, y: y };
      applyWorldStyle(x, y, zoom);
      if (t < 1) autoPanAnimRef.current = requestAnimationFrame(step);
      else autoPanAnimRef.current = null;
    }
    autoPanAnimRef.current = requestAnimationFrame(step);
  }

  // When the source node is a Tool Agent ("Tool Calling"), describe its real
  // output shape AND the metadata of the tools wired to its bottom anchor, so a
  // node built on its output (e.g. an Excel writer) can shape COMPATIBLE data
  // instead of guessing. Returns null for non-agent sources.
  //   output shape: { answer, toolCalls:[{ tool, input, output }] }
  //   per tool: title, purpose (generationPrompt), inputDescription, a minimized
  //             sample of its last run output (the true output shape).
  function buildSourceToolAgentMeta(connector) {
    var currentState = workflowStateRef.current || {};
    // Resolve nodes from the SAME snapshot the connections come from, so a tool
    // just wired isn't missed by a staler nodeById.
    var nodeMap = {};
    (currentState.nodes || []).forEach(function(n) { if (n && n.id) nodeMap[String(n.id)] = n; });
    var srcNode = nodeMap[String(connector.nodeId)] || nodeById[String(connector.nodeId)];
    if (!isToolAgentNode(srcNode)) return null;
    var tools = [];
    (currentState.connections || []).forEach(function(c) {
      // Tool wires leave the agent's BOTTOM anchor.
      if (!c || String(c.from) !== String(connector.nodeId)) return;
      if (String(c.fromAnchor || '') !== 'bottom') return;
      var toolNode = nodeMap[String(c.to)];
      if (!toolNode) return;
      var sampleOutput = nodeOutputByIdRef.current[String(c.to)];
      tools.push({
        title: toolNode.title || toolNode.label || 'Tool',
        purpose: String(toolNode.agentDescription || toolNode.generationPrompt || toolNode.subtitle || '').trim(),
        inputDescription: String(toolNode.inputDescription || '').trim(),
        outputSample: sampleOutput !== undefined ? minimizeArrays(sampleOutput) : undefined
      });
    });
    return { tools: tools };
  }

  // Snapshot of the source node's context: what flows into the new node and
  // what variables exist. Used BOTH by the discovery chat (as prompt context)
  // and by codegen (/custom-objects/generate). One builder, no duplication.
  function buildSingleNodeContext(connector, isToolMode) {
    if (!connector) return null;
    var currentState = workflowStateRef.current || {};
    var srcNode = nodes.find(function(n) { return n.id === connector.nodeId; });
    // Resolve the incoming input (inputs[0]) the SAME way the new-node generator
    // does (useAutoBuildWorkflow.buildGenerateContext): prefer the source node's
    // REAL run output, and only fall back to the synthetic Start shape when the
    // Start node has no run output yet. (Previously a Start source always used
    // the synthetic shape and ignored real values the user had run with.)
    var runOutput = nodeOutputByIdRef.current[connector.nodeId];
    var rawSourceOutput = runOutput !== undefined
      ? runOutput
      : (srcNode && srcNode.icon === 'start' ? getStartNodeOutput(srcNode) : undefined);
    // The planner only needs the data's shape + a sample, not the full payload.
    // A big output (e.g. an 856-record catalog) would blow the request cap (413),
    // so trim arrays/base64 with the shared minimizer (a no-op on non-objects).
    var sourceOutput = minimizeArrays(rawSourceOutput);
    var directlyConnectedToStart = !!(srcNode && srcNode.icon === 'start');
    var nodeFlow = buildUpstreamNodeFlow(connector.nodeId);

    var sourceVariables = {};
    (currentState.nodes || []).forEach(function(node) {
      if (!node || node.icon !== 'variables') return;
      var name = String(node.title || '').trim();
      if (!name) return;
      var pv = node.paramValues || {};
      var dataType = String(pv.dataType || 'string');
      // Runtime variable values are keyed by NAME (variableValuesByName), not by
      // node id — a var written during a run (esp. a Temporary var with no static
      // value) only shows up there. Prefer it, then the node-id output, then the
      // static paramValue. Without this the balloon sees a blank var and asks the
      // user to paste a shape that's already on the canvas.
      var runtimeByName = variableValuesByNameRef && variableValuesByNameRef.current
        ? variableValuesByNameRef.current[name]
        : undefined;
      var runtimeById = nodeOutputByIdRef.current[node.id];
      var effectiveValue = runtimeByName !== undefined ? runtimeByName
        : (runtimeById !== undefined ? runtimeById : pv.value);
      // A variable can hold a big array — trim it with the shared minimizer so it
      // can't bloat the context past the cap (a no-op on non-objects).
      sourceVariables[name] = {
        dataType: dataType,
        value: parseVariableContextValue(dataType, minimizeArrays(effectiveValue))
      };
    });

    var startParams = null;
    var startNode = (currentState.nodes || []).find(function(n) { return n && n.icon === 'start'; });
    if (startNode && Array.isArray(startNode.hyperparameters) && startNode.hyperparameters.length) {
      startParams = {};
      startNode.hyperparameters.forEach(function(p) {
        if (!p || !p.key) return;
        startParams[p.key] = (startNode.paramValues && startNode.paramValues[p.key] !== undefined)
          ? startNode.paramValues[p.key]
          : (p.default !== undefined ? p.default : '');
      });
    }

    return {
      sourceNodeId: connector.nodeId,
      sourceNodeTitle: connector.nodeTitle,
      nodeFlow: nodeFlow,
      // TOOL mode: the agent passes inputs[0] (shape <any_shape>) per call — it is
      // NOT this source node's output. Withhold sourceOutput so the planner never
      // treats the Tool Agent's output as the tool's input shape. The toolMode flag
      // lets the planner inject the <any_shape> framing instead.
      sourceOutput: isToolMode ? undefined : sourceOutput,
      toolMode: !!isToolMode,
      // When the source is a Tool Agent, the output is { answer, toolCalls[] } and
      // the wired tools carry their own shapes — pass both so a node built on the
      // agent's output shapes compatible data. (Not relevant in tool mode, where
      // the new node IS a tool of the agent, not a consumer of its output.)
      sourceToolAgent: isToolMode ? null : buildSourceToolAgentMeta(connector),
      sourceVariables: sourceVariables,
      startParams: startParams,
      // Same hint the generator sends so codegen knows inputs[0] is the Start
      // input shape (it then reads Start params instead of inventing fields).
      directlyConnectedToStart: isToolMode ? false : directlyConnectedToStart
    };
  }

  // System-object kinds (variable, condition, etc.) create instantly here.
  // Custom/agent kinds run through the AI Flow Builder chat (probing + Q&A) in
  // the balloon, which calls finalizeSingleNode(spec) once the node spec is ready.
  function generateAiNodeFromPrompt() {
    if (!aiPromptState || aiPromptState.loading) return;
    if (!isSystemObjectKind(aiPromptState.nodeKind)) return;

    var capturedConnector = aiPromptState.futureConnector;
    var capturedInsertContext = aiPromptState.insertConnectionId ? {
      insertConnectionId: aiPromptState.insertConnectionId,
      insertTargetNodeId: aiPromptState.insertTargetNodeId,
      insertTargetAnchor: aiPromptState.insertTargetAnchor
    } : null;

    var iconName = getSystemObjectIconFromKind(aiPromptState.nodeKind);
    var sysTpl = findSystemObjectTemplateByIcon(iconName);
    if (!sysTpl) {
      setAiPromptState(function(prev) { return prev ? Object.assign({}, prev, { error: 'Unknown system object.' }) : prev; });
      return;
    }
    var sysClone = Object.assign({}, sysTpl, { id: sysTpl.id + '-' + Date.now().toString(36) });
    setAiPromptState(null);
    createNodeFromAiTemplate(sysClone, capturedConnector, capturedInsertContext);
  }

  // Shared codegen request — one builder for the balloon (finalizeSingleNode)
  // and the Workflow Copilot (generateNodeAfterSource). Resolves to a ready
  // node template.
  function requestNodeGeneration(opts) {
    var promptText = opts.prompt;
    var isAgentMode = !!opts.agentMode;
    var isToolMode = !!opts.toolMode;
    var responseFields = opts.responseFields;
    var connector = opts.connector;
    var ctx = opts.ctx;

    var generatePromise;
    if (props.onGenerateWithAiPrompt) {
      generatePromise = Promise.resolve(props.onGenerateWithAiPrompt({
        prompt: promptText,
        agentMode: isAgentMode,
        toolMode: isToolMode,
        responseFields: responseFields,
        sourceNodeId: connector.nodeId,
        sourceAnchor: connector.anchor,
        sourceNodeTitle: connector.nodeTitle,
        nodeFlow: ctx ? ctx.nodeFlow : null,
        sourceOutput: ctx ? ctx.sourceOutput : undefined,
        sourceToolAgent: ctx ? ctx.sourceToolAgent : null,
        sourceVariables: ctx ? ctx.sourceVariables : {}
      }));
    } else {
      generatePromise = request('/custom-objects/generate', {
        method: 'POST',
        body: {
          prompt: promptText,
          agentMode: isAgentMode,
          toolMode: isToolMode,
          // Single-node codegen is always Sonnet (see pickModelForAttempt).
          modelTier: 'sonnet',
          responseFields: responseFields,
          context: {
            sourceNodeId: connector.nodeId,
            sourceNodeTitle: connector.nodeTitle,
            nodeFlow: ctx ? ctx.nodeFlow : null,
            sourceOutput: ctx ? ctx.sourceOutput : undefined,
            sourceToolAgent: ctx ? ctx.sourceToolAgent : null,
            sourceVariables: ctx ? ctx.sourceVariables : {},
            startParams: ctx ? ctx.startParams : null,
            directlyConnectedToStart: ctx ? !!ctx.directlyConnectedToStart : false
          }
        }
      }).then(function(data) {
        return buildGeneratedTemplate(data, promptText, { agentMode: isAgentMode, responseFields: responseFields });
      });
    }

    return generatePromise.then(function(result) {
      if (result && result.kind === 'workflow-node' && result.id) return result;
      return buildGeneratedTemplate(result, promptText, { agentMode: isAgentMode, responseFields: responseFields });
    });
  }

  // Called by the balloon's chat when the discovery model has emitted a <node>
  // spec ({ prompt, agentMode, responseFields }). Runs the SAME codegen the
  // planner build uses per node, then drops the node on the canvas.
  function finalizeSingleNode(spec) {
    if (!aiPromptState || !spec) return;

    var capturedConnector = aiPromptState.futureConnector;
    var capturedPosition = aiPromptState.position;
    var capturedInsertContext = aiPromptState.insertConnectionId ? {
      insertConnectionId: aiPromptState.insertConnectionId,
      insertTargetNodeId: aiPromptState.insertTargetNodeId,
      insertTargetAnchor: aiPromptState.insertTargetAnchor
    } : null;
    var capturedModelTier = aiPromptState.modelTier || 'balanced';
    var capturedInsertTargetTitle = aiPromptState.insertTargetTitle || '';
    // Tool mode is driven by the selected kind ('tool'), not just by where the
    // balloon was opened — the dropdown lets the user pick a tool node there.
    var capturedToolMode = aiPromptState.nodeKind === 'tool';
    // Whether the balloon was opened from a Tool Agent's "+ add tool" anchor
    // (kept so an error-retry can restore the tool node availability + context).
    var capturedFromToolAnchor = !!aiPromptState.toolMode;

    var promptText = String(spec.prompt || '').trim();
    if (!promptText) return;
    // Tool nodes are ALWAYS agent nodes (server coerces too — keep icon in sync).
    var isAgentMode = capturedToolMode ? true : !!spec.agentMode;
    var responseFields = (Array.isArray(spec.responseFields) ? spec.responseFields : [])
      .map(function(f) { return String(f || '').trim(); })
      .filter(Boolean);
    if (!responseFields.length) responseFields = capturedToolMode ? ['output'] : ['result'];

    var ctx = aiPromptState.singleNodeContext || buildSingleNodeContext(capturedConnector);

    var codingCardPos = getFutureNodePosition({ size: { width: 220, height: 80 } }, capturedConnector);
    setAiPromptState(null);
    setAiCodingState({ position: codingCardPos, futureConnector: capturedConnector });
    autoPanToCodeCard(codingCardPos);

    var generatePromise = requestNodeGeneration({
      prompt: promptText,
      agentMode: isAgentMode,
      toolMode: capturedToolMode,
      responseFields: responseFields,
      connector: capturedConnector,
      ctx: ctx
    });

    generatePromise.then(function(template) {
      if (!template || !template.id) {
        throw new Error('AI did not return a valid node.');
      }
      setAiCodingState(null);
      createNodeFromAiTemplate(template, capturedConnector, capturedInsertContext);
    }).catch(function(error) {
      var message = error && error.message ? error.message : 'Could not generate node.';
      setAiCodingState(null);
      setAiPromptState({
        futureConnector: capturedConnector,
        position: capturedPosition,
        prompt: promptText,
        loading: false,
        error: message,
        agentMode: isAgentMode,
        modelTier: capturedModelTier,
        responseFields: responseFields,
        fieldsEdited: true,
        // Preserve the tool node kind + tool-anchor flag so a retry stays a tool.
        nodeKind: capturedToolMode ? 'tool' : 'custom',
        toolMode: capturedFromToolAnchor,
        singleNodeContext: ctx,
        insertConnectionId: capturedInsertContext ? capturedInsertContext.insertConnectionId : null,
        insertTargetNodeId: capturedInsertContext ? capturedInsertContext.insertTargetNodeId : null,
        insertTargetAnchor: capturedInsertContext ? capturedInsertContext.insertTargetAnchor : null,
        insertTargetTitle: capturedInsertTargetTitle
      });
    });
  }

  // Synthesize the connector the "+" button after a source node would carry
  // (the attach point sits to its right). Shared by generateNodeAfterSource and
  // the copilot's single-node context builder so both anchor the new node the
  // same way. Returns null when the source node no longer exists.
  function buildAfterSourceConnector(sourceNodeId) {
    var id = String(sourceNodeId || '').trim();
    if (!id) return null;
    var currentState = workflowStateRef.current || {};
    var srcNode = (currentState.nodes || []).find(function(n) { return n && n.id === id; });
    if (!srcNode) return null;
    var srcPos = srcNode.position || { left: 0, top: 0 };
    var srcSize = srcNode.size || {};
    var attachPoint = {
      x: (srcPos.left || 0) + (srcSize.width || 220) + 60,
      y: (srcPos.top || 0) + ((srcSize.height || 80) / 2)
    };
    return {
      id: 'copilot-after-' + id,
      nodeId: id,
      nodeTitle: srcNode.title || srcNode.label || 'Node',
      anchor: 'right',
      to: attachPoint,
      buttonLeft: attachPoint.x - (FUTURE_CONNECTOR_BUTTON_WIDTH / 2),
      buttonTop: attachPoint.y - (FUTURE_CONNECTOR_BUTTON_HEIGHT / 2)
    };
  }

  // Build the single-node planner context for the node the copilot wants to add
  // AFTER `sourceNodeId` — the SAME context shape the balloon feeds the
  // discovery stream (input shape, variables, upstream flow). Returns null if
  // the source node no longer exists.
  function buildSingleNodeContextForSource(sourceNodeId) {
    var connector = buildAfterSourceConnector(sourceNodeId);
    return connector ? buildSingleNodeContext(connector, false) : null;
  }

  // Programmatic entry for the Workflow Copilot: same codegen + insertion as
  // finalizeSingleNode, but driven by an explicit spec instead of the balloon
  // state. The copilot runs its own probe + Q&A discovery first (via the
  // embedded AiFlowChatPanel) and hands the finished { prompt, agentMode,
  // responseFields } spec here. Shows the same "coding…" card while generating.
  // Resolves { nodeId, title } once the node is on the canvas (connected after
  // the source node); rejects on generation failure.
  function generateNodeAfterSource(args) {
    var a = args || {};
    var sourceNodeId = String(a.sourceNodeId || '').trim();
    var promptText = String(a.prompt || '').trim();
    var isAgentMode = !!a.agentMode;
    var responseFields = (Array.isArray(a.responseFields) ? a.responseFields : [])
      .map(function(f) { return String(f || '').trim(); })
      .filter(Boolean);
    if (!responseFields.length) responseFields = isAgentMode ? [autoFieldName(promptText)] : ['result'];

    if (!promptText) return Promise.reject(new Error('prompt is required'));
    var connector = buildAfterSourceConnector(sourceNodeId);
    if (!connector) return Promise.reject(new Error('No node with id "' + sourceNodeId + '" to attach after'));

    var ctx = buildSingleNodeContext(connector, false);
    var codingCardPos = getFutureNodePosition({ size: { width: 220, height: 80 } }, connector);
    setAiCodingState({ position: codingCardPos, futureConnector: connector });
    autoPanToCodeCard(codingCardPos);

    return requestNodeGeneration({
      prompt: promptText,
      agentMode: isAgentMode,
      toolMode: false,
      responseFields: responseFields,
      connector: connector,
      ctx: ctx
    }).then(function(template) {
      if (!template || !template.id) throw new Error('AI did not return a valid node.');
      setAiCodingState(null);
      var newNodeId = createNodeFromAiTemplate(template, connector, null);
      // Attaching on a condition branch: stamp yes/no onto the connection
      // createNodeFromAiTemplate just made AND move it to the branch's output
      // dot (input ta="left" → YES=top / NO=right; ta="top" → YES=right /
      // NO=bottom — same mapping the planner uses).
      var branch = a.branch === 'yes' || a.branch === 'no' ? a.branch : null;
      // Classifier/Router label output: the caller resolved the label to its
      // "classifier-N" anchor — stamp it the same way.
      var labelAnchor = !branch && typeof a.fromAnchor === 'string' && a.fromAnchor.indexOf('classifier-') === 0 ? a.fromAnchor : null;
      if ((branch || labelAnchor) && newNodeId) {
        setWorkflowState(function(prev) {
          var incoming = (prev.connections || []).find(function(c) { return c.to === sourceNodeId; });
          var ta = incoming && incoming.toAnchor ? incoming.toAnchor : 'left';
          var branchAnchor = labelAnchor || (ta === 'top'
            ? (branch === 'yes' ? 'right' : 'bottom')
            : (branch === 'yes' ? 'top' : 'right'));
          return Object.assign({}, prev, {
            connections: (prev.connections || []).map(function(c) {
              return (c.from === sourceNodeId && c.to === newNodeId && !c.branch)
                ? Object.assign({}, c, branch ? { branch: branch, fromAnchor: branchAnchor } : { fromAnchor: branchAnchor })
                : c;
            })
          });
        });
      }
      return { nodeId: newNodeId, title: template.title || template.label || 'Node' };
    }).catch(function(error) {
      setAiCodingState(null);
      throw error;
    });
  }

  return {
    aiPromptState: aiPromptState,
    aiCodingState: aiCodingState,
    setAiPromptState: setAiPromptState,
    setAiCodingState: setAiCodingState,
    handleGenerateFlowWithAi: handleGenerateFlowWithAi,
    handleInsertNodeOnConnection: handleInsertNodeOnConnection,
    closeAiPrompt: closeAiPrompt,
    updateAiPrompt: updateAiPrompt,
    updateAiAgentMode: updateAiAgentMode,
    updateAiModelTier: updateAiModelTier,
    updateAiResponseFields: updateAiResponseFields,
    updateAiNodeKind: updateAiNodeKind,
    buildGeneratedTemplate: buildGeneratedTemplate,
    createNodeFromAiTemplate: createNodeFromAiTemplate,
    autoRunNewNode: autoRunNewNode,
    autoPanToCodeCard: autoPanToCodeCard,
    generateAiNodeFromPrompt: generateAiNodeFromPrompt,
    finalizeSingleNode: finalizeSingleNode,
    generateNodeAfterSource: generateNodeAfterSource,
    // Exposed so the Workflow Copilot can build the SAME source-node context
    // (input shape, variables, upstream flow) the balloon feeds the single-node
    // planner discovery stream — then run probe + Q&A before generateNodeAfterSource.
    buildSingleNodeContextForSource: buildSingleNodeContextForSource
  };
}

module.exports = useAiFlowGeneration;
